/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Profile, profileTypeMap } from './types';
import { authenticatedGet } from 'remote/api';
import { supabase } from 'supabase/Client';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import {
  SignInWithPasswordlessCredentials,
  VerifyOtpParams,
} from '@supabase/supabase-js';
import { ADVISOR_ID, ADVISOR_TYPE, USER_TYPE } from 'constants/constants';

import { Store } from 'storage';

interface AuthProviderContext {
  isLoading: boolean;
  profile: Profile | null;
  getProfile: (role?: string) => void;
  login: (credentials: SignInWithPasswordlessCredentials) => Promise<void>;
  verifyOtp: (params: VerifyOtpParams) => Promise<void>;
  logout: () => Promise<void>;
  advisorSignin: (
    credentials: SignInWithPasswordlessCredentials,
  ) => Promise<void>;
  addAdvisorEmail: (email: string) => void;
  advisorEmail: string | null;
}

const AuthContext = createContext<AuthProviderContext>({
  isLoading: false,
  profile: null,
  getProfile: (role?: string) => {},
  login: async (credentials: SignInWithPasswordlessCredentials) => {},
  verifyOtp: async (params: VerifyOtpParams) => {},
  logout: async () => {},
  advisorSignin: async () => {},
  addAdvisorEmail: (email: string) => {},
  advisorEmail: null,
});

const store = new Store();

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const navigate = useNavigate();
  const [advisorEmail, setAdvisorEmail] = useState<string | null>(null);

  const getProfile = useCallback(async (role?: string) => {
    try {
      const roleType = !role ? '' : role;
      const res = await authenticatedGet(`/profile?role=${roleType}`);
      setProfile(res.data);
      if (res.data.advisor && res.data.advisor.length > 0) {
        store.set(ADVISOR_ID, res.data.advisor[0].uuid);
      }
      if (res.data.profile_details.profile_type) {
        store.set(
          USER_TYPE,
          profileTypeMap[res.data.profile_details.profile_type],
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
    }
  }, []);

  const addAdvisorEmail = (email: string) => {
    setAdvisorEmail(email);
  };

  const login = async (credentials: SignInWithPasswordlessCredentials) => {
    const { error } = await supabase.auth.signInWithOtp({
      ...credentials,
      options: {
        ...credentials.options,
        shouldCreateUser: true,
      },
    });
    if (error) throw error;
  };

  const advisorSignin = async (
    credentials: SignInWithPasswordlessCredentials,
  ) => {
    const { error } = await supabase.auth.signInWithOtp({
      ...credentials,
      options: {
        ...credentials.options,
      },
    });

    if (error) throw error;
  };

  const verifyOtp = async (params: VerifyOtpParams) => {
    const { error } = await supabase.auth.verifyOtp(params);
    if (error) throw error;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    if (store.get(USER_TYPE) === ADVISOR_TYPE) {
      store.remove(ADVISOR_ID);
      localStorage.clear();
      setProfile(null);
      navigate('/advisor/signin');
      return;
    }
    setProfile(null);
    navigate('/customer/signin');
  };

  useEffect(() => {
    if (profile) {
      return;
    }
    const fetchUser = async () => {
      setIsLoading(true);
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        const role = store.get(USER_TYPE) || '';
        await getProfile(role);
      }
      setIsLoading(false);
    };

    const { data: authListener } = supabase.auth.onAuthStateChange(e => {
      if (e === 'INITIAL_SESSION' || (!profile && e === 'SIGNED_IN')) {
        fetchUser();
      } else if (e === 'SIGNED_OUT') {
        setProfile(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [profile]);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        profile,
        getProfile,
        logout,
        login,
        verifyOtp,
        advisorSignin,
        addAdvisorEmail,
        advisorEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthProviderContext => {
  const context = useContext(AuthContext);
  return context;
};

export { AuthProvider, useAuth };
